import React, { useEffect, useRef, useState } from 'react';
import type { Credential, Snapshot } from '../shared/protocol';
import { Icon } from './design/components/Icon';
import { AudioPlayback, type AudioState, type AudioStatus } from './audio-playback';
import { MusicPlayback } from './music-playback';
import './audio.css';

const labels: Record<AudioStatus, string> = {
  off: 'Audio off', ready: 'Audio ready', loading: 'Loading audio…', playing: 'Audio playing', paused: 'Audio paused',
  muted: 'Audio muted', complete: 'Audio ready', blocked: 'Press OK or click on this TV to start sound', unavailable: 'Audio unavailable · game continues',
};

export function DisplayAudio({ credential, snapshot, now, online }: { credential: Credential; snapshot: Snapshot | null; now: number; online: boolean }) {
  const media = useRef<HTMLAudioElement>(null);
  const controller = useRef<AudioPlayback | null>(null);
  const soundtrack = useRef<MusicPlayback | null>(null);
  const enabled = true;
  const { muted, volume } = snapshot?.audioSettings ?? { muted: false, volume: 0.8 };
  const [status, setStatus] = useState<AudioStatus>('ready');
  const [musicStatus, setMusicStatus] = useState<AudioStatus>('ready');
  const state: AudioState = { snapshot, now, online, enabled, muted, volume };
  const latest = useRef(state);
  latest.current = state;
  useEffect(() => {
    const playback = new AudioPlayback(media.current!, credential.token, setStatus, {
      fetch: window.fetch.bind(window), createObjectURL: URL.createObjectURL, revokeObjectURL: URL.revokeObjectURL, clock: () => performance.now(),
    });
    controller.current = playback;
    const background = new MusicPlayback(credential.token, setMusicStatus, {
      fetch: window.fetch.bind(window), clock: () => performance.now(), createContext: () => new AudioContext(),
    });
    soundtrack.current = background;
    playback.update(latest.current);
    background.update(latest.current);
    background.enable();
    const gestures = ['pointerdown', 'click', 'keydown'] as const;
    const removeGestures = () => { for (const event of gestures) document.removeEventListener(event, unlock, true); };
    const unlock = (event: Event) => {
      if (!event.isTrusted) return;
      playback.retry();
      background.enable();
      removeGestures();
    };
    for (const event of gestures) document.addEventListener(event, unlock, true);
    return () => { removeGestures(); controller.current = null; soundtrack.current = null; playback.dispose(); background.dispose(); };
  }, [credential.token]);
  useEffect(() => { controller.current?.update(state); }, [snapshot, now, online, enabled, muted, volume]);
  useEffect(() => { soundtrack.current?.setDucked(status === 'playing'); soundtrack.current?.update(state); }, [snapshot, now, online, enabled, muted, volume, status]);
  const enable = () => {
    controller.current?.retry();
    soundtrack.current?.enable();
  };
  return <div className="display-audio">
    <audio ref={media} preload="none" aria-hidden="true" />
    {status === 'blocked' || musicStatus === 'blocked' || status === 'unavailable' || musicStatus === 'unavailable' ? <button className="display-audio-toggle" onClick={enable}><Icon name="volume" size={16} />{status === 'unavailable' || musicStatus === 'unavailable' ? 'Retry audio' : 'Start sound'}</button> : null}
    <span className="display-audio-status" role="status">{muted ? 'Audio muted by host' : labels[status === 'blocked' || musicStatus === 'blocked' ? 'blocked' : status === 'unavailable' || musicStatus === 'unavailable' ? 'unavailable' : musicStatus === 'playing' ? 'playing' : status]}</span>
  </div>;
}
